export const PublicEnvKeys = {
  SENTRY_ENABLED: 'IS_SENTRY_ENABLED',
  BRANCH_NAME: 'GIT_BRANCH_NAME',
  ASSETS_PATH: 'UA_ASSETS_PATH',
  DPOP_AUTH_ENABLED: 'UA_DPOP_AUTH_ENABLED',
} as const;

const parseEnvValue = <T extends string | number | boolean | object>(
  value: string | undefined,
  defaultValue: T,
): T => {
  if (value === undefined || value === '') {
    return defaultValue;
  }

  // Try parsing as JSON first (for objects, arrays, or JSON-encoded primitives)
  // Use a sentinel value to detect if JSON parsing actually succeeded
  const sentinel = Symbol('json-parse-failed');
  let jsonParsed: unknown = sentinel;
  try {
    jsonParsed = JSON.parse(value);
  } catch {
    // JSON parsing failed, will handle below
  }

  // If JSON parsing succeeded, return the parsed value
  if (jsonParsed !== sentinel) {
    return jsonParsed as T;
  }

  // If JSON parsing failed, handle based on default value type
  if (typeof defaultValue === 'boolean') {
    // Handle boolean strings: 'true', 'false', '1', '0'
    const lowerValue = value.toLowerCase().trim();
    if (lowerValue === 'true' || lowerValue === '1') {
      return true as T;
    }
    if (lowerValue === 'false' || lowerValue === '0') {
      return false as T;
    }
    return defaultValue;
  }

  if (typeof defaultValue === 'number') {
    // Try parsing as number
    const numValue = Number(value);
    if (!Number.isNaN(numValue) && value.trim() !== '') {
      return numValue as T;
    }
    return defaultValue;
  }

  if (typeof defaultValue === 'string') {
    // Return as-is for strings
    return value as T;
  }

  // For objects, if JSON parsing failed, return default
  return defaultValue;
};

export const getPublicEnv = <T extends string | number | boolean | object>(
  key: (typeof PublicEnvKeys)[keyof typeof PublicEnvKeys],
  defaultValue: T,
): T => {
  if (typeof window === 'undefined') {
    const envValue = process.env[key];
    return parseEnvValue(envValue, defaultValue);
  }
  // Script.tsx assigns these onto `window` at runtime; nothing declares them on the
  // Window type, so the index is an implicit `any`. This package compiles with
  // `strict: false` and so never saw it — a strict consumer importing anything that
  // reaches this file (apps/tensor, via utils/cspHeaders) fails on TS7015. Cast rather
  // than augment the global: a `declare global` here would leak these keys onto Window
  // for every app that imports the package.
  const windowValue = (window as unknown as Record<string, unknown>)[key];
  // window values are always strings when injected via Script.tsx
  return parseEnvValue(
    typeof windowValue === 'string' ? windowValue : String(windowValue ?? ''),
    defaultValue,
  );
};
